// Líneas de producción — pantalla 2 del rediseño del directivo (mockups D5).
//
// Estado de TODAS las líneas sobre el período, agrupadas Planta → Área → Línea:
//   - PLANTAS DESPLEGABLES: cada planta colapsa/expande; las que no tienen
//     capturas en el período arrancan colapsadas con su aviso (nada de bajar
//     entre filas vacías). Orden: mayor producción primero.
//   - Toggle Tabla | Gráfico + búsqueda + filtro de TURNO (end-to-end).
//     Granularidad no aplica acá: no hay eje temporal, la unidad es la línea.
//   - Por LÍNEA solo cumplimiento vs CAPACIDAD (WS-4); el programa del cliente
//     vive en el subheader de cada ÁREA — y se OCULTA con turno activo (el
//     programa es diario, no distingue turno).
//   - Tiendas: cadencia libre → regla interina meta = producido ("100%*"),
//     insumos COBRADOS suman al valor; fuera de la vista Gráfico (sin meta).
//   - Pareto por causal madre (valor no producido) + Distribución de paros.
//   - Click en una línea → drill hora a hora (/directivo/lineas/:id).

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type { DirectivoLineas, LineaEstado } from 'schemas';
import {
  Bar,
  CartesianGrid,
  ComposedChart,
  LabelList,
  Line,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { Card } from '../../components/ui/card';
import { ConectividadBadge } from '../../components/ui/conectividad-badge';
import { Icon } from '../../components/ui/icon';
import { SegmentedControl } from '../../components/ui/segmented-control';
import { Spinner } from '../../components/ui/spinner';
import { DirectivoLayout } from '../../components/layout/DirectivoLayout';
import { useDirectivoLineas } from '../../lib/api/queries';
import { ParosEnCursoChip } from './components/ParosEnCursoChip';
import { cn } from '../../lib/utils';
import { semaforoCumplimiento } from '../../lib/semaforo';
import { BarraPeriodo, enlaceConPeriodo, usePeriodo } from './lib/periodo';
import { copM, horasMin, miles, pctEntero } from './lib/formato';
import { hayPendiente, textoPendiente } from './lib/pendiente';
import { LabelValorMeta, LabelValorProduccion } from './components/chartLabels';

type Vista = 'tabla' | 'grafico';
type TurnoSel = 'todos' | '1' | '2' | '3';
type ActividadSel = 'todas' | 'activas' | 'vacias';

/** Línea sin capturas en el rango/turno filtrado. */
function esVacia(l: LineaEstado): boolean {
  return l.unidades <= 0 && l.horas_trabajadas <= 0;
}

function utilidadDe(l: LineaEstado): number | null {
  if (l.valor_cop === null || l.costo_mo_cop === null) return null;
  return l.valor_cop + (l.ingreso_insumos_cop ?? 0) - l.costo_mo_cop;
}

// DN5: semáforo de OPERACIÓN compartido (90/70), el mismo del coordinador.
function toneClass(pct: number): string {
  const tone = semaforoCumplimiento(pct);
  return tone === 'ok' ? 'bg-ok' : tone === 'warn' ? 'bg-warn' : 'bg-crit';
}

function dotDe(l: LineaEstado): string {
  if (l.paro_activo) return 'bg-crit';
  if (l.es_tienda || l.meta_capacidad_unds <= 0) return 'bg-fg-tertiary';
  return toneClass((l.unidades / l.meta_capacidad_unds) * 100);
}

export function LineasProduccionPage() {
  const [plantaSel, setPlantaSel] = useState<string>('todas');
  const [vista, setVista] = useState<Vista>('tabla');
  const [busqueda, setBusqueda] = useState('');
  const [turnoSel, setTurnoSel] = useState<TurnoSel>('todos');
  const [actividadSel, setActividadSel] = useState<ActividadSel>('todas');
  const ctrl = usePeriodo(undefined, 'hoy', { url: true });
  const { periodo } = ctrl;
  const turno = turnoSel === 'todos' ? null : Number(turnoSel);

  const lineas = useDirectivoLineas({ desde: periodo.desde, hasta: periodo.hasta, turno });

  return (
    <DirectivoLayout>
      {/* ── Barra de filtros ── */}
      <div className="mb-3 flex flex-wrap items-center gap-3">
        <BarraPeriodo ctrl={ctrl} />
        <div className="flex-1" />
        {lineas.data && lineas.data.plantas.length > 1 ? (
          <div className="flex items-center gap-2">
            <span className="text-[10px] font-medium uppercase tracking-[0.1em] text-fg-tertiary">
              Planta
            </span>
            <SegmentedControl
              value={plantaSel}
              onChange={setPlantaSel}
              size="sm"
              options={[
                { value: 'todas', label: `Todas (${lineas.data.plantas.length})` },
                ...lineas.data.plantas.map((p) => ({ value: p.id, label: p.nombre })),
              ]}
            />
          </div>
        ) : null}
      </div>
      <p className="mb-4 text-[11px] text-fg-tertiary">
        Líneas de producción /{' '}
        {plantaSel === 'todas'
          ? 'Todas'
          : (lineas.data?.plantas.find((p) => p.id === plantaSel)?.nombre ?? '')}
      </p>

      {lineas.isLoading ? (
        <div className="flex justify-center py-16">
          <Spinner />
        </div>
      ) : lineas.isError || !lineas.data ? (
        <Card className="p-6 text-sm text-fg-secondary">
          No se pudo cargar el estado de líneas. Intente de nuevo en unos segundos.
        </Card>
      ) : (
        <LineasContenido
          data={lineas.data}
          plantaSel={plantaSel}
          vista={vista}
          onVista={setVista}
          busqueda={busqueda}
          onBusqueda={setBusqueda}
          turnoSel={turnoSel}
          onTurno={setTurnoSel}
          actividadSel={actividadSel}
          onActividad={setActividadSel}
          periodoLabel={periodo.label}
          enlaceLinea={(id) => enlaceConPeriodo(`/directivo/lineas/${id}`, ctrl)}
        />
      )}
    </DirectivoLayout>
  );
}

function LineasContenido({
  data,
  plantaSel,
  vista,
  onVista,
  busqueda,
  onBusqueda,
  turnoSel,
  onTurno,
  actividadSel,
  onActividad,
  periodoLabel,
  enlaceLinea,
}: {
  data: DirectivoLineas;
  plantaSel: string;
  vista: Vista;
  onVista: (v: Vista) => void;
  busqueda: string;
  onBusqueda: (q: string) => void;
  turnoSel: TurnoSel;
  onTurno: (t: TurnoSel) => void;
  actividadSel: ActividadSel;
  onActividad: (a: ActividadSel) => void;
  periodoLabel: string;
  /** Ruta al detalle de una línea, con el período actual en el querystring. */
  enlaceLinea: (id: string) => string;
}) {
  const navigate = useNavigate();
  const conCostos = data.puede_ver_costos;
  const conTurno = data.turno !== null;
  const q = busqueda.trim().toLowerCase();

  const plantas = data.plantas.filter((p) => plantaSel === 'todas' || p.id === plantaSel);
  const areaNombre = new Map(data.areas.map((a) => [a.area_id, a.nombre]));
  const coincide = (l: LineaEstado) =>
    q === '' ||
    l.codigo.toLowerCase().includes(q) ||
    (l.area_id ? (areaNombre.get(l.area_id) ?? '').toLowerCase().includes(q) : false);

  // Base = planta + búsqueda; el filtro de actividad se aplica encima y sus
  // conteos salen de la base (para que los números de los chips no "bailen").
  const lineasBase = data.lineas.filter(
    (l) => plantas.some((p) => p.id === l.planta_id) && coincide(l),
  );
  const vaciasCount = lineasBase.filter(esVacia).length;
  const lineasVisibles = lineasBase.filter((l) =>
    actividadSel === 'todas' ? true : actividadSel === 'vacias' ? esVacia(l) : !esVacia(l),
  );
  const plantaNombre =
    plantaSel === 'todas'
      ? 'Todas las plantas'
      : (data.plantas.find((p) => p.id === plantaSel)?.nombre ?? '');
  const filtrosLabel = `${periodoLabel} · ${plantaNombre} · ${
    turnoSel === 'todos' ? 'Todos los turnos' : `Turno ${turnoSel}`
  }`;

  // ── Pareto y distribución (siguen el filtro de planta) ──
  const paretoAgg = new Map<
    string,
    { causal: string; color: string | null; minutos: number; eventos: number; valor: number }
  >();
  for (const p of data.pareto) {
    if (plantaSel !== 'todas' && p.planta_id !== plantaSel) continue;
    const acc = paretoAgg.get(p.causal) ?? {
      causal: p.causal,
      color: p.color,
      minutos: 0,
      eventos: 0,
      valor: 0,
    };
    acc.minutos += p.minutos;
    acc.eventos += p.eventos;
    acc.valor += p.valor_cop ?? 0;
    if (!acc.color && p.color) acc.color = p.color;
    paretoAgg.set(p.causal, acc);
  }
  const pareto = [...paretoAgg.values()].sort((a, b) => b.minutos - a.minutos).slice(0, 6);
  const maxParetoMin = pareto[0]?.minutos ?? 0;

  const turnoAgg = new Map<number, number>();
  for (const t of data.distribucion.por_turno) {
    if (plantaSel !== 'todas' && t.planta_id !== plantaSel) continue;
    turnoAgg.set(t.turno, (turnoAgg.get(t.turno) ?? 0) + t.minutos);
  }
  const maxTurno = Math.max(1, ...turnoAgg.values());

  const horaAgg = new Map<string, number>();
  for (const h of data.distribucion.por_hora) {
    if (plantaSel !== 'todas' && h.planta_id !== plantaSel) continue;
    horaAgg.set(h.hora, (horaAgg.get(h.hora) ?? 0) + h.minutos);
  }
  const horas24 = Array.from({ length: 24 }, (_, i) => String(i).padStart(2, '0'));
  const maxHora = Math.max(1, ...horaAgg.values());
  const picoHora = horas24.reduce(
    (best, h) => ((horaAgg.get(h) ?? 0) > (horaAgg.get(best) ?? 0) ? h : best),
    '00',
  );

  return (
    <>
      <Card className="p-5">
        <div className="mb-4 flex flex-wrap items-center gap-3">
          <div>
            <h2 className="text-sm font-medium text-fg">Estado de líneas</h2>
            <p className="mt-0.5 text-[11px] text-fg-tertiary">Filtrado: {filtrosLabel}</p>
          </div>
          <div className="flex-1" />
          <div className="flex items-center gap-2">
            <span className="text-[10px] font-medium uppercase tracking-[0.1em] text-fg-tertiary">
              Turno
            </span>
            <SegmentedControl
              value={turnoSel}
              onChange={onTurno}
              size="sm"
              options={[
                { value: 'todos', label: 'Todos' },
                { value: '1', label: 'T1' },
                { value: '2', label: 'T2' },
                { value: '3', label: 'T3' },
              ]}
            />
          </div>
          <SegmentedControl
            value={vista}
            onChange={onVista}
            size="sm"
            options={[
              { value: 'tabla', label: 'Tabla' },
              { value: 'grafico', label: 'Gráfico' },
            ]}
          />
          <SegmentedControl
            value={actividadSel}
            onChange={onActividad}
            size="sm"
            options={[
              { value: 'todas', label: `Todas (${lineasBase.length})` },
              { value: 'activas', label: `Con actividad (${lineasBase.length - vaciasCount})` },
              { value: 'vacias', label: `Vacías (${vaciasCount})` },
            ]}
          />
          <input
            type="search"
            value={busqueda}
            onChange={(e) => onBusqueda(e.target.value)}
            placeholder="Buscar línea o área..."
            className="h-8 w-48 rounded border border-border bg-surface-input px-2.5 text-xs text-fg placeholder:text-fg-tertiary"
          />
        </div>

        {vista === 'tabla' ? (
          <TablaLineas
            data={data}
            plantas={plantas}
            lineasVisibles={lineasVisibles}
            conCostos={conCostos}
            conTurno={conTurno}
            expandirTodo={actividadSel === 'vacias'}
            onLinea={(id) => navigate(enlaceLinea(id))}
          />
        ) : (
          <GraficoLineas lineas={lineasVisibles} plantas={plantas} />
        )}
      </Card>

      {/* ── Pareto + Distribución ── */}
      <div className="mt-5 grid grid-cols-1 gap-5 xl:grid-cols-2">
        <Card className="p-5">
          <div className="mb-1 flex flex-wrap items-baseline gap-3">
            <h2 className="text-sm font-medium text-fg">Pareto por causal</h2>
            <span className="text-[11px] text-fg-tertiary">
              horas de paro · valor no producido · eventos
            </span>
          </div>
          <p className="mb-4 text-[11px] font-medium text-fg-secondary">Filtrado: {filtrosLabel}</p>
          {pareto.length === 0 ? (
            <p className="text-sm text-fg-tertiary">Sin paros que afecten disponibilidad</p>
          ) : (
            <div className="flex flex-col gap-2.5">
              {pareto.map((c) => (
                <div key={c.causal} className="flex items-center gap-3">
                  <span
                    className="inline-block size-2 shrink-0 rounded-full"
                    style={{ background: c.color ?? 'var(--color-fg-tertiary)' }}
                  />
                  <span className="w-56 truncate text-[12px] text-fg-secondary">{c.causal}</span>
                  <div className="h-2 flex-1 overflow-hidden rounded-sm bg-surface-overlay">
                    <div
                      className="h-full rounded-sm"
                      style={{
                        width: `${maxParetoMin > 0 ? Math.max(4, (c.minutos / maxParetoMin) * 100) : 0}%`,
                        background: c.color ?? 'var(--color-fg-tertiary)',
                      }}
                    />
                  </div>
                  <span className="whitespace-nowrap font-mono text-[11px] tabular-nums text-fg-secondary">
                    {horasMin(c.minutos)}
                    {conCostos ? ` · ${copM(c.valor)}` : ''} · {c.eventos} ev
                  </span>
                </div>
              ))}
            </div>
          )}
        </Card>

        <Card className="p-5">
          <h2 className="mb-1 text-sm font-medium text-fg">Distribución de paros</h2>
          <p className="mb-4 text-[11px] font-medium text-fg-secondary">Filtrado: {filtrosLabel}</p>
          <p className="mb-1.5 text-[10px] font-medium uppercase tracking-[0.1em] text-fg-tertiary">
            Por turno
          </p>
          <div className="flex flex-col gap-1.5">
            {[1, 2, 3].map((t) => {
              const min = turnoAgg.get(t) ?? 0;
              return (
                <div key={t} className="flex items-center gap-3">
                  <span className="w-6 font-mono text-[11px] text-fg-secondary">T{t}</span>
                  <div className="h-2 flex-1 overflow-hidden rounded-sm bg-surface-overlay">
                    <div
                      className="h-full rounded-sm bg-fg-tertiary"
                      style={{ width: `${Math.max(min > 0 ? 4 : 0, (min / maxTurno) * 100)}%` }}
                    />
                  </div>
                  <span className="w-16 text-right font-mono text-[11px] tabular-nums text-fg-secondary">
                    {min > 0 ? horasMin(min) : '—'}
                  </span>
                </div>
              );
            })}
          </div>
          <p className="mb-1.5 mt-4 text-[10px] font-medium uppercase tracking-[0.1em] text-fg-tertiary">
            Por hora del día{(horaAgg.get(picoHora) ?? 0) > 0 ? ` · pico ${picoHora}:00` : ''}
          </p>
          <div className="grid gap-0.5" style={{ gridTemplateColumns: 'repeat(24, 1fr)' }}>
            {horas24.map((h) => {
              const min = horaAgg.get(h) ?? 0;
              return (
                <div
                  key={h}
                  title={`${h}:00 — ${min > 0 ? horasMin(min) : 'sin paros'}`}
                  className="h-6 rounded-[2px] bg-fg"
                  style={{ opacity: min > 0 ? 0.15 + 0.85 * (min / maxHora) : 0.06 }}
                />
              );
            })}
          </div>
          <div className="mt-1 flex justify-between text-[9px] text-fg-tertiary">
            <span>00:00</span>
            <span>06:00</span>
            <span>12:00</span>
            <span>18:00</span>
            <span>23:00</span>
          </div>
        </Card>
      </div>
    </>
  );
}

// ============================================================================
// Vista TABLA — Planta (desplegable) → Área → Líneas
// ============================================================================

interface TotalesLineas {
  unidades: number;
  meta: number;
  parosMin: number;
  horas: number;
  valor: number;
  costo: number;
  sinPrecio: number;
  /** DN10: capturado sin validar — declarado, NO suma a valor/costo. */
  pendBloques: number;
  pendPaq: number;
  pendValor: number;
}

function totalesDe(ls: LineaEstado[]): TotalesLineas {
  return {
    unidades: ls.reduce((s, l) => s + l.unidades, 0),
    meta: ls.reduce((s, l) => s + l.meta_capacidad_unds, 0),
    parosMin: ls.reduce((s, l) => s + l.paros_min, 0),
    horas: ls.reduce((s, l) => s + l.horas_trabajadas, 0),
    valor: ls.reduce((s, l) => s + (l.valor_cop ?? 0) + (l.ingreso_insumos_cop ?? 0), 0),
    costo: ls.reduce((s, l) => s + (l.costo_mo_cop ?? 0), 0),
    sinPrecio: ls.reduce((s, l) => s + l.skus_sin_precio, 0),
    pendBloques: ls.reduce((s, l) => s + (l.pendiente_validacion?.bloques ?? 0), 0),
    pendPaq: ls.reduce((s, l) => s + (l.pendiente_validacion?.paquetes ?? 0), 0),
    pendValor: ls.reduce((s, l) => s + (l.pendiente_validacion?.valor_producido ?? 0), 0),
  };
}

const TH = 'px-3 py-2.5 text-[10.5px] font-semibold uppercase tracking-[0.08em] text-fg-secondary';
const TD_NUM = 'whitespace-nowrap px-3 py-2.5 text-right font-mono text-[12px] tabular-nums';

function TablaLineas({
  data,
  plantas,
  lineasVisibles,
  conCostos,
  conTurno,
  expandirTodo,
  onLinea,
}: {
  data: DirectivoLineas;
  plantas: Array<{ id: string; nombre: string; codigo: string }>;
  lineasVisibles: LineaEstado[];
  conCostos: boolean;
  conTurno: boolean;
  /** Viendo solo vacías: las plantas arrancan expandidas aunque estén en 0. */
  expandirTodo: boolean;
  onLinea: (id: string) => void;
}) {
  // Desplegables: override manual por planta; por defecto las plantas SIN
  // capturas en el período arrancan colapsadas (con su aviso en el header).
  const [abiertas, setAbiertas] = useState<Record<string, boolean>>({});
  const toggle = (id: string, porDefecto: boolean) =>
    setAbiertas((prev) => ({ ...prev, [id]: !(prev[id] ?? porDefecto) }));

  const tot = totalesDe(lineasVisibles);

  const celdaMoney = (v: number | null, opts?: { tone?: boolean; bold?: boolean }) => (
    <td
      className={cn(
        TD_NUM,
        opts?.bold && 'font-semibold',
        opts?.tone && v !== null && v < 0 ? 'text-crit' : 'text-fg',
      )}
    >
      {conCostos && v !== null ? copM(v) : '—'}
    </td>
  );

  // Mayor producción primero: la planta viva arriba, la vacía al final.
  const plantasOrdenadas = plantas
    .map((p) => {
      const propias = lineasVisibles.filter((l) => l.planta_id === p.id);
      return { p, propias, tp: totalesDe(propias) };
    })
    .filter(({ propias }) => propias.length > 0)
    .sort((a, b) => b.tp.unidades - a.tp.unidades);

  // Encabezados PEGAJOSOS: la tabla scrollea dentro de su contenedor (max-h)
  // y el thead queda fijo arriba. El "borde" inferior del header va como
  // sombra: con border-collapse los bordes no acompañan al sticky.
  const TH_STICKY = cn(
    TH,
    'sticky top-0 z-10 bg-surface-elevated shadow-[0_1px_0_var(--color-border-stronger)]',
  );
  return (
    <div className="max-h-[68vh] overflow-auto">
      <table className="w-full min-w-[920px] border-collapse">
        <thead>
          <tr>
            <th className={cn(TH_STICKY, 'text-left')}>Línea</th>
            <th className={cn(TH_STICKY, 'text-left')}>Estado</th>
            <th className={cn(TH_STICKY, 'text-right')}>Producido</th>
            <th className={cn(TH_STICKY, 'text-right')}>Meta</th>
            <th className={cn(TH_STICKY, 'text-left')}>Cumpl. capacidad</th>
            <th className={cn(TH_STICKY, 'text-right')}>Paros</th>
            <th className={cn(TH_STICKY, 'text-right')}>Horas trab.</th>
            <th
              className={cn(TH_STICKY, 'text-right')}
              title="Sobre producción validada por supervisión (misma base del informe gerencial). Lo pendiente de validar se declara en el total y no suma."
            >
              Valor prod.
            </th>
            <th
              className={cn(TH_STICKY, 'text-right')}
              title="Mano de obra de los bloques validados: operarios × horas reales × costo hora-persona vigente."
            >
              Costo MO
            </th>
            <th
              className={cn(TH_STICKY, 'text-right')}
              title="Valor producido − costo MO, sobre producción validada."
            >
              Utilidad
            </th>
          </tr>
        </thead>
        <tbody>
          {plantasOrdenadas.map(({ p, propias, tp }) => {
            const sinCapturas = tp.unidades <= 0 && tp.horas <= 0;
            const abierta = abiertas[p.id] ?? (expandirTodo || !sinCapturas);
            const cumplP = pctEntero(tp.unidades, tp.meta);
            const areasDe = data.areas.filter(
              (a) => a.planta_id === p.id && propias.some((l) => l.area_id === a.area_id),
            );
            const sinArea = propias.filter(
              (l) => !l.area_id || !areasDe.some((a) => a.area_id === l.area_id),
            );
            return [
              <tr
                key={p.id}
                onClick={() => toggle(p.id, expandirTodo || !sinCapturas)}
                className="cursor-pointer select-none border-y border-border bg-surface-overlay/60 transition-colors hover:bg-surface-overlay"
                title={abierta ? 'Colapsar planta' : 'Expandir planta'}
              >
                <td className="px-3 py-3">
                  <span className="inline-flex items-center gap-2 text-[13px] font-semibold uppercase tracking-[0.04em] text-fg">
                    <Icon
                      name={abierta ? 'chevron-down' : 'chevron-right'}
                      size={14}
                      stroke={2}
                      className="text-fg-tertiary"
                    />
                    {p.nombre}
                  </span>
                </td>
                <td className="px-3 py-3">
                  {sinCapturas ? (
                    <span className="whitespace-nowrap font-mono text-[10px] uppercase tracking-[0.06em] text-fg-tertiary">
                      Sin capturas en el período
                    </span>
                  ) : (
                    <span className="font-mono text-[10px] text-fg-tertiary">
                      {propias.length} líneas
                    </span>
                  )}
                </td>
                <td className={cn(TD_NUM, 'font-semibold text-fg')}>
                  {sinCapturas ? '—' : miles(tp.unidades)}
                </td>
                <td className={cn(TD_NUM, 'text-fg-secondary')}>
                  {sinCapturas ? '—' : miles(tp.meta)}
                </td>
                <td className="px-3 py-3">{sinCapturas ? null : <BarraCumpl pct={cumplP} />}</td>
                <td className={cn(TD_NUM, 'text-fg-secondary')}>
                  {tp.parosMin > 0 ? horasMin(tp.parosMin) : '—'}
                </td>
                <td className={cn(TD_NUM, 'text-fg-secondary')}>
                  {sinCapturas ? '—' : `${Math.round(tp.horas)}h`}
                </td>
                {celdaMoney(conCostos && !sinCapturas ? tp.valor : null, { bold: true })}
                {celdaMoney(conCostos && !sinCapturas ? tp.costo : null)}
                {celdaMoney(conCostos && !sinCapturas ? tp.valor - tp.costo : null, {
                  tone: true,
                  bold: true,
                })}
              </tr>,
              ...(abierta
                ? [
                    ...areasDe.flatMap((a) => {
                      const deArea = propias.filter((l) => l.area_id === a.area_id);
                      const ta = totalesDe(deArea);
                      const esTiendas = deArea.every((l) => l.es_tienda);
                      const cliPct = pctEntero(a.real_planif_unds, a.programa_unds);
                      return [
                        <tr
                          key={a.area_id}
                          className="border-b border-border/60 bg-surface-overlay/20"
                        >
                          <td colSpan={7} className="px-3 pb-1.5 pt-3">
                            <span className="font-mono text-[10px] uppercase tracking-[0.08em] text-fg-tertiary">
                              <span className="font-semibold text-fg-secondary">{a.nombre}</span>
                              {!conTurno && a.programa_unds > 0
                                ? ` — ${miles(a.real_planif_unds)} / ${miles(a.programa_unds)} paq · ${cliPct !== null ? `${cliPct}% cliente` : 'n/s'}`
                                : ''}
                              {` · ${horasMin(ta.parosMin)} paro · ${Math.round(ta.horas)}h trab`}
                              {conCostos
                                ? ` · ${copM(ta.valor)} · ${copM(ta.costo)} · ${copM(ta.valor - ta.costo)}`
                                : ''}
                            </span>
                          </td>
                          <td colSpan={3} className="px-3 pb-1.5 pt-3 text-right">
                            {esTiendas && ta.sinPrecio > 0 ? (
                              <span className="font-mono text-[10px] text-warn">
                                ⚠ {ta.sinPrecio} SKUs sin precio — valor parcial · cadencia libre:
                                meta = producido (regla interina)
                              </span>
                            ) : null}
                          </td>
                        </tr>,
                        ...deArea
                          .slice()
                          .sort((a1, b1) => b1.unidades - a1.unidades)
                          .map((l) => (
                            <FilaLinea
                              key={l.linea_id}
                              linea={l}
                              conCostos={conCostos}
                              onClick={() => onLinea(l.linea_id)}
                              celdaMoney={celdaMoney}
                            />
                          )),
                      ];
                    }),
                    ...sinArea.map((l) => (
                      <FilaLinea
                        key={l.linea_id}
                        linea={l}
                        conCostos={conCostos}
                        onClick={() => onLinea(l.linea_id)}
                        celdaMoney={celdaMoney}
                      />
                    )),
                  ]
                : []),
            ];
          })}
          <tr className="border-t-2 border-border-stronger bg-surface-overlay/40">
            <td className="px-3 py-3 text-[12px] font-semibold uppercase tracking-[0.04em] text-fg">
              Total
            </td>
            <td />
            <td className={cn(TD_NUM, 'font-semibold text-fg')}>{miles(tot.unidades)}</td>
            <td className={cn(TD_NUM, 'text-fg-secondary')}>{miles(tot.meta)}</td>
            <td className="px-3 py-3">
              <BarraCumpl pct={pctEntero(tot.unidades, tot.meta)} />
            </td>
            <td className={cn(TD_NUM, 'text-fg-secondary')}>
              <span className="inline-flex flex-wrap items-center justify-end gap-1.5">
                {tot.parosMin > 0 ? horasMin(tot.parosMin) : '—'}
                {/* DN1: total de paros abiertos del período (todo el alcance) — aparte. */}
                <ParosEnCursoChip cantidad={data.paros_en_curso} />
              </span>
            </td>
            <td className={cn(TD_NUM, 'text-fg-secondary')}>{Math.round(tot.horas)}h</td>
            {celdaMoney(conCostos ? tot.valor : null, { bold: true })}
            {celdaMoney(conCostos ? tot.costo : null)}
            {celdaMoney(conCostos ? tot.valor - tot.costo : null, { tone: true, bold: true })}
          </tr>
          {conCostos ? (
            // DN10: el $ de arriba es sobre producción validada; lo pendiente se
            // declara aquí con su tamaño y no suma.
            <tr className="bg-surface-overlay/40">
              <td
                colSpan={10}
                className={cn(
                  'px-3 pb-3 text-[11px]',
                  hayPendiente({ bloques: tot.pendBloques }) ? 'text-warn' : 'text-fg-tertiary',
                )}
                style={{ fontVariantNumeric: 'tabular-nums' }}
              >
                Base del dinero: producción validada por supervisión.{' '}
                {textoPendiente(
                  { bloques: tot.pendBloques, paquetes: tot.pendPaq },
                  copM(tot.pendValor),
                )}
              </td>
            </tr>
          ) : null}
        </tbody>
      </table>
    </div>
  );
}

function FilaLinea({
  linea: l,
  conCostos,
  onClick,
  celdaMoney,
}: {
  linea: LineaEstado;
  conCostos: boolean;
  onClick: () => void;
  celdaMoney: (v: number | null, opts?: { tone?: boolean; bold?: boolean }) => React.ReactElement;
}) {
  const cumpl = l.es_tienda ? null : pctEntero(l.unidades, l.meta_capacidad_unds);
  const utilidad = utilidadDe(l);
  return (
    <tr
      onClick={onClick}
      className="cursor-pointer border-b border-border/40 transition-colors hover:bg-surface-overlay/60"
      title="Ver detalle hora a hora"
    >
      <td className="py-2.5 pl-8 pr-3 font-mono text-[12px] font-semibold uppercase tracking-[0.03em] text-fg">
        {l.codigo}
      </td>
      <td className="px-3 py-2.5">
        {l.conectividad ? (
          // Línea de máquina: punto de estado + señal compacta (separados).
          <span className="inline-flex items-center gap-2">
            <span className={cn('inline-block size-2 rounded-full', dotDe(l))} />
            <ConectividadBadge
              conectividad={l.conectividad}
              ultimoLatidoAt={l.ultimo_latido_at}
              compact
            />
          </span>
        ) : (
          <span className={cn('inline-block size-2 rounded-full', dotDe(l))} />
        )}
      </td>
      <td className={cn(TD_NUM, 'text-fg')}>
        {miles(l.unidades)}
        {l.es_tienda ? ' unds' : ''}
      </td>
      <td className={cn(TD_NUM, 'text-fg-secondary')}>
        {l.es_tienda ? '= prod.' : miles(l.meta_capacidad_unds)}
      </td>
      <td className="px-3 py-2.5">
        {l.es_tienda ? (
          <span
            className="font-mono text-[12px] text-fg-secondary"
            title="Regla interina de tiendas: meta = producido"
          >
            100%*
          </span>
        ) : (
          <BarraCumpl pct={cumpl} />
        )}
      </td>
      <td className={cn(TD_NUM, 'text-fg-secondary')}>
        {l.paros_min > 0 ? horasMin(l.paros_min) : '—'}
      </td>
      <td className={cn(TD_NUM, 'text-fg-secondary')}>{Math.round(l.horas_trabajadas)}h</td>
      {celdaMoney(
        conCostos && l.valor_cop !== null ? l.valor_cop + (l.ingreso_insumos_cop ?? 0) : null,
      )}
      {celdaMoney(l.costo_mo_cop)}
      {celdaMoney(utilidad, { tone: true })}
    </tr>
  );
}

function BarraCumpl({ pct }: { pct: number | null }) {
  if (pct === null) return <span className="font-mono text-[12px] text-fg-tertiary">n/s</span>;
  return (
    <span className="inline-flex items-center gap-2">
      <span className="h-1.5 w-20 overflow-hidden rounded-sm bg-surface-overlay">
        <span
          className={cn('block h-full rounded-sm', toneClass(pct))}
          style={{ width: `${Math.min(100, pct)}%` }}
        />
      </span>
      <span className="font-mono text-[12px] tabular-nums text-fg">{pct}%</span>
    </span>
  );
}

// ============================================================================
// Vista GRÁFICO — por línea, solo vs capacidad (WS-4); tiendas → ver tabla
// ============================================================================

type SerieGrafico = 'meta' | 'producido' | 'capacidad';

const SERIES_GRAFICO: Array<{
  key: SerieGrafico;
  label: string;
  tipo: 'bar' | 'line';
  color: string;
}> = [
  { key: 'capacidad', label: 'Cumplimiento capacidad', tipo: 'line', color: 'var(--color-info)' },
  { key: 'producido', label: 'Producido', tipo: 'bar', color: 'var(--color-fg-tertiary)' },
  { key: 'meta', label: 'Meta capacidad', tipo: 'bar', color: 'var(--color-border-strong)' },
];

function GraficoLineas({
  lineas,
  plantas,
}: {
  lineas: LineaEstado[];
  plantas: Array<{ id: string; nombre: string }>;
}) {
  const [ocultas, setOcultas] = useState<Set<SerieGrafico>>(() => new Set());
  const toggle = (k: SerieGrafico) =>
    setOcultas((prev) => {
      const next = new Set(prev);
      if (next.has(k)) next.delete(k);
      else next.add(k);
      return next;
    });
  const visible = (k: SerieGrafico) => !ocultas.has(k);

  const ordenPlanta = new Map(plantas.map((p, i) => [p.id, i]));
  const data = lineas
    .filter((l) => !l.es_tienda && (l.unidades > 0 || l.meta_capacidad_unds > 0))
    .sort(
      (a, b) =>
        (ordenPlanta.get(a.planta_id) ?? 0) - (ordenPlanta.get(b.planta_id) ?? 0) ||
        b.unidades - a.unidades,
    )
    .map((l) => ({
      codigo: l.codigo,
      meta: Math.round(l.meta_capacidad_unds),
      producido: l.unidades,
      capacidad_pct: pctEntero(l.unidades, l.meta_capacidad_unds),
    }));

  if (data.length === 0) {
    return (
      <div className="flex h-64 items-center justify-center text-sm text-fg-tertiary">
        Sin líneas para graficar con los filtros actuales
      </div>
    );
  }

  return (
    <div>
      <ResponsiveContainer width="100%" height={330}>
        <ComposedChart data={data} margin={{ top: 20, right: 0, left: -8, bottom: 0 }} barGap={2}>
          <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" vertical={false} />
          <XAxis
            dataKey="codigo"
            tick={{ fontSize: 10, fill: 'var(--color-fg-secondary)' }}
            axisLine={{ stroke: 'var(--color-border)' }}
            tickLine={false}
            interval={0}
            angle={data.length > 10 ? -35 : 0}
            textAnchor={data.length > 10 ? 'end' : 'middle'}
            height={data.length > 10 ? 52 : 30}
          />
          <YAxis
            yAxisId="unds"
            tickFormatter={(v) => miles(Number(v))}
            tick={{ fontSize: 10, fill: 'var(--color-fg-tertiary)' }}
            axisLine={false}
            tickLine={false}
            width={52}
          />
          <YAxis
            yAxisId="pct"
            orientation="right"
            domain={[0, 130]}
            tickFormatter={(v) => `${v}%`}
            tick={{ fontSize: 10, fill: 'var(--color-fg-tertiary)' }}
            axisLine={false}
            tickLine={false}
            width={40}
          />
          <Tooltip
            cursor={{ fill: 'var(--color-surface-overlay)' }}
            contentStyle={{
              background: 'var(--color-surface-elevated)',
              border: '1px solid var(--color-border)',
              borderRadius: 8,
              fontSize: 12,
            }}
            formatter={(v, name) =>
              String(name) === 'Cumplimiento capacidad'
                ? [`${Math.round(Number(v))}%`, String(name)]
                : [`${miles(Number(v))} paq`, String(name)]
            }
          />
          <Bar
            yAxisId="unds"
            dataKey="meta"
            name="Meta capacidad"
            fill="var(--color-border-strong)"
            radius={[2, 2, 0, 0]}
            maxBarSize={30}
            hide={!visible('meta')}
          >
            <LabelList dataKey="meta" content={LabelValorMeta} />
          </Bar>
          <Bar
            yAxisId="unds"
            dataKey="producido"
            name="Producido"
            fill="var(--color-fg-tertiary)"
            radius={[2, 2, 0, 0]}
            maxBarSize={30}
            hide={!visible('producido')}
          >
            {/* Valor adentro si cabe; arriba de la barra si la columna es bajita */}
            <LabelList dataKey="producido" content={LabelValorProduccion} />
          </Bar>
          <Line
            yAxisId="pct"
            dataKey="capacidad_pct"
            name="Cumplimiento capacidad"
            stroke="var(--color-info)"
            strokeWidth={1.5}
            strokeDasharray="5 4"
            dot={{ r: 2.5, fill: 'var(--color-info)', strokeWidth: 0 }}
            connectNulls
            hide={!visible('capacidad')}
          >
            <LabelList
              dataKey="capacidad_pct"
              position="top"
              offset={8}
              formatter={(v: unknown) =>
                v === null || v === undefined ? '' : `${Math.round(Number(v))}%`
              }
              style={{ fontSize: 10, fill: 'var(--color-info)', fontWeight: 600 }}
            />
          </Line>
        </ComposedChart>
      </ResponsiveContainer>

      {/* Leyendas-filtro (mismo lenguaje que el gráfico central del Dashboard) */}
      <div className="mt-3 flex flex-wrap gap-1.5">
        {SERIES_GRAFICO.map((s) => {
          const on = visible(s.key);
          return (
            <button
              key={s.key}
              type="button"
              onClick={() => toggle(s.key)}
              title={on ? 'Clic para ocultar esta serie' : 'Clic para mostrar esta serie'}
              className={cn(
                'inline-flex items-center gap-2 rounded border px-2.5 py-1 text-[11px] transition-all',
                on
                  ? 'border-border-strong bg-surface-overlay text-fg'
                  : 'border-border text-fg-tertiary opacity-60 hover:opacity-90',
              )}
            >
              {s.tipo === 'line' ? (
                <span
                  className="inline-block h-0 w-4 border-t-2"
                  style={{ borderColor: s.color, borderStyle: 'dashed' }}
                />
              ) : (
                <span
                  className="inline-block size-2.5 rounded-[2px]"
                  style={{ background: s.color }}
                />
              )}
              {s.label}
            </button>
          );
        })}
      </div>
      <p className="mt-2 text-[11px] text-fg-tertiary">
        Por línea el cumplimiento es vs CAPACIDAD (el programa del cliente vive por área — ver
        tabla). Tiendas: cadencia libre, sin meta — ver tabla.
      </p>
    </div>
  );
}
